import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * U9 (2026-07-05): re-skinned to emit the HOUSE button system (`.btn` +
 * BEM variants, defined in app.css — DESIGN_HANDOFF.md §5). The previous
 * Tailwind utility strings were INERT (tailwind.css was never imported;
 * the dependency was removed in U8), so every <Button> rendered as a
 * near-unstyled native button. Variant names keep the shadcn API so no
 * call site changes:
 *   default     → .btn--primary  (filled, cool-blue "confirm/save/send")
 *   destructive → .btn--danger
 *   outline     → .btn           (the house default IS the outline look)
 *   secondary   → .btn
 *   ghost       → .btn--ghost
 *   link        → .btn--ghost
 *   sm/lg/icon  → .btn--sm / .btn--lg / .btn--icon
 */
const buttonVariants = cva("btn", {
  variants: {
    variant: {
      default: "btn--primary",
      destructive: "btn--danger",
      outline: "",
      secondary: "",
      ghost: "btn--ghost",
      link: "btn--ghost",
    },
    size: {
      default: "",
      sm: "btn--sm",
      lg: "btn--lg",
      icon: "btn--icon",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
})

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
